import AddPhotos from '../steps/AddPhotos';
import AllowContactByPhone from '../steps/AllowContactByPhone';
import AmenitiesInterior from '../steps/AmenitiesInterior';
import AmenitiesProperty from '../steps/AmenitiesProperty';
import CostsAndFees from '../steps/CostsAndFees';
import DescribeProperty from '../steps/DescribeProperty';
import DescriptionAndAmenities from '../steps/DescriptionAndAmenities';
import FastPostReview from '../steps/FastPostReview';
import FinancialExpectations from '../steps/FinancialExpectations';
import HidePropertyAddress from '../steps/HidePropertyAddress';
import LeaseDuration from '../steps/LeaseDuration';
import LeaseTerms from '../steps/LeaseTerms';
import MonthlyRentAndSecurityDeposit from '../steps/MonthlyRentAndSecurityDeposit';
import MultiplexListingPublished from '../steps/MultiplexListingPublished';
import MultiplexOverview from '../steps/MultiplexOverview';
import MultiplexPropertyDetails from '../steps/MultiplexPropertyDetails';
import PayAndPublish from '../steps/PayAndPublish';
import PetPolicy from '../steps/PetPolicy';
import PetPolicyAndPetFee from '../steps/PetPolicyAndPetFee';
import PhoneNumber from '../steps/PhoneNumber';
import PropertyInfo from '../steps/PropertyInfo';
import RelistAdditionalDetails from '../steps/RelistAdditionalDetails';
import RelistConfirmDetails from '../steps/RelistConfirmDetails';
import RentDetailsAndCosts from '../steps/RentDetailsAndCosts';
import RenterPreferences from '../steps/RenterPreferences';
import Review from '../steps/Review';
import RoomCurrentPets from '../steps/RoomCurrentPets';
import RoomDescription from '../steps/RoomDescription';
import RoomHowManyRoommates from '../steps/RoomHowManyRoommates';
import RoommateDescription from '../steps/RoommateDescription';
import RoomUtilitiesAmount from '../steps/RoomUtilitiesAmount';
import RoomWhoIsListingProperty from '../steps/RoomWhoIsListingProperty';
import ScreeningCriteriaAndPolicies from '../steps/ScreeningCriteriaAndPolicies';
import TourSchedulingAvailability from '../steps/TourSchedulingAvailability';
import TourSchedulingBookingMethod from '../steps/TourSchedulingBookingMethod';
import TourSchedulingTimezone from '../steps/TourSchedulingTimezone';
import WhenIsPropertyAvailable from '../steps/WhenIsPropertyAvailable';
import WhoIsListingProperty from '../steps/WhoIsListingProperty';
import { FlowType } from '../types';
import { PROGRESS_STEP_KEYS, STEP_KEYS } from './stepKeys';

import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { WizardBodyTemplateProps, WizardStepType } from '../../WizardStepController/types';
import type { FeatureFlags, UserSelections } from '../types';

type Property = GetUserPropertyDetailsResponseWebDto;

// Step ordering by flow type (matches legacy config.ts order)
const STEP_ORDER: Record<FlowType, Array<string>> = {
  [FlowType.STANDARD]: [
    // Property info group (config.ts lines 57-118)
    STEP_KEYS.ROOM_WHO_IS_LISTING_PROPERTY,
    STEP_KEYS.ROOM_DESCRIPTION,
    STEP_KEYS.PROPERTY_INFO,
    STEP_KEYS.HIDE_PROPERTY_ADDRESS,
    STEP_KEYS.ROOM_HOW_MANY_ROOMMATES,
    STEP_KEYS.ROOMMATE_DESCRIPTION,
    STEP_KEYS.RENTER_PREFERENCES,
    STEP_KEYS.ROOM_CURRENT_PETS,
    // Rent details group (config.ts lines 121-150)
    STEP_KEYS.MONTHLY_RENT_AND_SECURITY_DEPOSIT,
    STEP_KEYS.ROOM_UTILITIES_AMOUNT,
    STEP_KEYS.WHEN_IS_PROPERTY_AVAILABLE,
    STEP_KEYS.PET_POLICY,
    // Media group (config.ts lines 152-159)
    STEP_KEYS.ADD_PHOTOS,
    // Amenities group (config.ts lines 161-176)
    STEP_KEYS.AMENITIES_INTERIOR,
    STEP_KEYS.AMENITIES_PROPERTY,
    // Screening criteria group (config.ts lines 178-193)
    STEP_KEYS.FINANCIAL_EXPECTATIONS,
    STEP_KEYS.PET_POLICY_AND_PET_FEE,
    // Costs & fees group (config.ts lines 195-203)
    STEP_KEYS.COSTS_AND_FEES,
    // Final details group (config.ts lines 205-267)
    STEP_KEYS.LEASE_DURATION,
    STEP_KEYS.LEASE_DESCRIPTION,
    STEP_KEYS.WHO_IS_LISTING_PROPERTY,
    STEP_KEYS.PHONE_NUMBER,
    STEP_KEYS.ALLOW_CONTACT_BY_PHONE,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY,
    STEP_KEYS.DESCRIBE_PROPERTY,
    STEP_KEYS.FAST_POST_REVIEW,
    // Review group (config.ts lines 269-275)
    STEP_KEYS.REVIEW,
    // Pay & publish group (config.ts lines 277-283)
    STEP_KEYS.PAY_AND_PUBLISH,
  ],
  [FlowType.AI_LISTING_CREATION]: [
    STEP_KEYS.PROPERTY_INFO,
    STEP_KEYS.RENT_DETAILS_AND_COSTS,
    STEP_KEYS.ADD_PHOTOS,
    STEP_KEYS.SCREENING_CRITERIA_AND_PET_POLICY,
    STEP_KEYS.DESCRIPTION_AND_AMENITIES,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY,
    STEP_KEYS.REVIEW_AI,
    STEP_KEYS.PAY_AND_PUBLISH_AI,
  ],
  [FlowType.RELISTING]: [
    STEP_KEYS.UPDATE_RECENTLY_CHANGED,
    STEP_KEYS.CONSIDER_ADDITIONAL_DETAILS,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE_RELIST,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    STEP_KEYS.REVIEW,
    STEP_KEYS.PAY_AND_PUBLISH,
  ],
  [FlowType.RELISTING_TOUR]: [
    STEP_KEYS.UPDATE_RECENTLY_CHANGED,
    STEP_KEYS.CONSIDER_ADDITIONAL_DETAILS,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE_RELIST,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    STEP_KEYS.REVIEW,
    STEP_KEYS.PAY_AND_PUBLISH,
  ],
  [FlowType.MULTIPLEX]: [
    STEP_KEYS.MULTIPLEX_PROPERTY_DETAILS,
    STEP_KEYS.MULTIPLEX_OVERVIEW,
    STEP_KEYS.REVIEW,
    STEP_KEYS.PAY_AND_PUBLISH,
    STEP_KEYS.MULTIPLEX_LISTING_PUBLISHED,
  ],
  [FlowType.ROOM_FOR_RENT_RYAN]: [
    // Room-for-rent property info (config.ts lines 57-118)
    STEP_KEYS.ROOM_WHO_IS_LISTING_PROPERTY,
    STEP_KEYS.ROOM_DESCRIPTION,
    STEP_KEYS.PROPERTY_INFO, // Shows "Confirm some facts about the room and property"
    STEP_KEYS.HIDE_PROPERTY_ADDRESS,
    STEP_KEYS.ROOM_HOW_MANY_ROOMMATES,
    STEP_KEYS.ROOMMATE_DESCRIPTION, // RYAN flow only
    STEP_KEYS.RENTER_PREFERENCES, // RYAN flow only
    STEP_KEYS.ROOM_CURRENT_PETS,
    // Rent details
    STEP_KEYS.MONTHLY_RENT_AND_SECURITY_DEPOSIT,
    STEP_KEYS.ROOM_UTILITIES_AMOUNT,
    STEP_KEYS.WHEN_IS_PROPERTY_AVAILABLE,
    STEP_KEYS.PET_POLICY,
    // Media
    STEP_KEYS.ADD_PHOTOS,
    // Amenities
    STEP_KEYS.AMENITIES_INTERIOR,
    STEP_KEYS.AMENITIES_PROPERTY,
    // Screening criteria
    STEP_KEYS.FINANCIAL_EXPECTATIONS,
    STEP_KEYS.PET_POLICY_AND_PET_FEE,
    // Costs & fees
    STEP_KEYS.COSTS_AND_FEES,
    // Final details (config.ts lines 205-267)
    STEP_KEYS.LEASE_DURATION,
    STEP_KEYS.LEASE_DESCRIPTION,
    STEP_KEYS.WHO_IS_LISTING_PROPERTY, // "Who is the contact person?"
    STEP_KEYS.PHONE_NUMBER,
    STEP_KEYS.ALLOW_CONTACT_BY_PHONE,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY,
    STEP_KEYS.FAST_POST_REVIEW,
    // Review & publish
    STEP_KEYS.REVIEW,
    STEP_KEYS.PAY_AND_PUBLISH,
  ],
  [FlowType.ROOM_FOR_RENT_IRENE]: [
    // Room-for-rent property info (no ROOMMATE_DESCRIPTION, no RENTER_PREFERENCES)
    STEP_KEYS.ROOM_WHO_IS_LISTING_PROPERTY,
    STEP_KEYS.ROOM_DESCRIPTION,
    STEP_KEYS.PROPERTY_INFO, // Shows "Confirm some facts about the room and property"
    STEP_KEYS.HIDE_PROPERTY_ADDRESS,
    STEP_KEYS.ROOM_HOW_MANY_ROOMMATES,
    STEP_KEYS.ROOM_CURRENT_PETS,
    // Rent details
    STEP_KEYS.MONTHLY_RENT_AND_SECURITY_DEPOSIT,
    STEP_KEYS.ROOM_UTILITIES_AMOUNT,
    STEP_KEYS.WHEN_IS_PROPERTY_AVAILABLE,
    STEP_KEYS.PET_POLICY,
    // Media
    STEP_KEYS.ADD_PHOTOS,
    // Amenities
    STEP_KEYS.AMENITIES_INTERIOR,
    STEP_KEYS.AMENITIES_PROPERTY,
    // Screening criteria
    STEP_KEYS.FINANCIAL_EXPECTATIONS,
    STEP_KEYS.PET_POLICY_AND_PET_FEE,
    // Costs & fees
    STEP_KEYS.COSTS_AND_FEES,
    // Final details (config.ts lines 205-267)
    STEP_KEYS.LEASE_DURATION,
    STEP_KEYS.LEASE_DESCRIPTION,
    STEP_KEYS.WHO_IS_LISTING_PROPERTY, // "Who is the contact person?"
    STEP_KEYS.PHONE_NUMBER,
    STEP_KEYS.ALLOW_CONTACT_BY_PHONE,
    STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD,
    STEP_KEYS.TOUR_SCHEDULING_TIMEZONE,
    STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY,
    STEP_KEYS.FAST_POST_REVIEW,
    // Review & publish
    STEP_KEYS.REVIEW,
    STEP_KEYS.PAY_AND_PUBLISH,
  ],
};

// Progress group configurations (matches legacy PROGRESS_STEP_CONFIG)
export const PROGRESS_GROUPS: Record<FlowType, Array<{ key: string; label: string }>> = {
  [FlowType.STANDARD]: [
    { key: PROGRESS_STEP_KEYS.PROPERTY_INFO, label: 'Property info' },
    { key: PROGRESS_STEP_KEYS.RENT_DETAILS, label: 'Rent details' },
    { key: PROGRESS_STEP_KEYS.MEDIA, label: 'Media' },
    { key: PROGRESS_STEP_KEYS.AMENITIES, label: 'Amenities' },
    { key: PROGRESS_STEP_KEYS.SCREENING_CRITERIA, label: 'Screening criteria' },
    { key: PROGRESS_STEP_KEYS.COSTS_AND_FEES, label: 'Costs & fees' },
    { key: PROGRESS_STEP_KEYS.FINAL_DETAILS, label: 'Final details' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
  ],
  [FlowType.AI_LISTING_CREATION]: [
    { key: PROGRESS_STEP_KEYS.PROPERTY_INFO, label: 'Property info' },
    { key: PROGRESS_STEP_KEYS.RENT_DETAILS_AND_COSTS, label: 'Rent & costs' },
    { key: PROGRESS_STEP_KEYS.MEDIA, label: 'Media' },
    { key: PROGRESS_STEP_KEYS.SCREENING_CRITERIA_AND_PET_POLICY, label: 'Screening criteria' },
    { key: PROGRESS_STEP_KEYS.DESCRIPTION_AND_AMENITIES, label: 'Description & amenities' },
    { key: PROGRESS_STEP_KEYS.FINAL_DETAILS, label: 'Touring' },
    { key: PROGRESS_STEP_KEYS.REVIEW_AND_PUBLISH, label: 'Review & publish' },
  ],
  [FlowType.RELISTING]: [
    { key: PROGRESS_STEP_KEYS.UPDATE, label: 'Update' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
  ],
  [FlowType.RELISTING_TOUR]: [
    { key: PROGRESS_STEP_KEYS.UPDATE, label: 'Update' },
    { key: PROGRESS_STEP_KEYS.TOUR_BOOKING, label: 'Tour booking' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
  ],
  [FlowType.MULTIPLEX]: [
    { key: PROGRESS_STEP_KEYS.PROPERTY_DETAILS, label: 'Property details' },
    { key: PROGRESS_STEP_KEYS.MULTIPLEX_OVERVIEW, label: 'Overview' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
    { key: PROGRESS_STEP_KEYS.PUBLISHED, label: 'Published' },
  ],
  [FlowType.ROOM_FOR_RENT_RYAN]: [
    { key: PROGRESS_STEP_KEYS.PROPERTY_INFO, label: 'Property info' },
    { key: PROGRESS_STEP_KEYS.RENT_DETAILS, label: 'Rent details' },
    { key: PROGRESS_STEP_KEYS.MEDIA, label: 'Media' },
    { key: PROGRESS_STEP_KEYS.AMENITIES, label: 'Amenities' },
    { key: PROGRESS_STEP_KEYS.SCREENING_CRITERIA, label: 'Screening criteria' },
    { key: PROGRESS_STEP_KEYS.COSTS_AND_FEES, label: 'Costs & fees' },
    { key: PROGRESS_STEP_KEYS.FINAL_DETAILS, label: 'Final details' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
  ],
  [FlowType.ROOM_FOR_RENT_IRENE]: [
    { key: PROGRESS_STEP_KEYS.PROPERTY_INFO, label: 'Property info' },
    { key: PROGRESS_STEP_KEYS.RENT_DETAILS, label: 'Rent details' },
    { key: PROGRESS_STEP_KEYS.MEDIA, label: 'Media' },
    { key: PROGRESS_STEP_KEYS.AMENITIES, label: 'Amenities' },
    { key: PROGRESS_STEP_KEYS.SCREENING_CRITERIA, label: 'Screening criteria' },
    { key: PROGRESS_STEP_KEYS.COSTS_AND_FEES, label: 'Costs & fees' },
    { key: PROGRESS_STEP_KEYS.FINAL_DETAILS, label: 'Final details' },
    { key: PROGRESS_STEP_KEYS.REVIEW, label: 'Review' },
    { key: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH, label: 'Publish' },
  ],
};

/**
 * All wizard steps with visibility conditions
 */
const ALL_STEPS: Record<
  string,
  WizardStepType & {
    shouldRender: (
      property: Property,
      flags: FeatureFlags,
      flowType?: FlowType,
      userSelections?: UserSelections,
    ) => boolean;
  }
> = {
  // Room for rent steps
  [STEP_KEYS.ROOM_WHO_IS_LISTING_PROPERTY]: {
    stepKey: STEP_KEYS.ROOM_WHO_IS_LISTING_PROPERTY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoomWhoIsListingProperty({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property) => !!property.roomForRent,
  },

  [STEP_KEYS.ROOM_DESCRIPTION]: {
    stepKey: STEP_KEYS.ROOM_DESCRIPTION,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoomDescription({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property) => !!property.roomForRent,
  },

  [STEP_KEYS.ROOM_HOW_MANY_ROOMMATES]: {
    stepKey: STEP_KEYS.ROOM_HOW_MANY_ROOMMATES,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoomHowManyRoommates({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property) => !!property.roomForRent,
  },

  [STEP_KEYS.ROOMMATE_DESCRIPTION]: {
    stepKey: STEP_KEYS.ROOMMATE_DESCRIPTION,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoommateDescription({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property, _flags, flowType) =>
      !!property.roomForRent && flowType === FlowType.ROOM_FOR_RENT_RYAN,
  },

  [STEP_KEYS.RENTER_PREFERENCES]: {
    stepKey: STEP_KEYS.RENTER_PREFERENCES,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RenterPreferences({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property, _flags, flowType) =>
      !!property.roomForRent && flowType === FlowType.ROOM_FOR_RENT_RYAN,
  },

  [STEP_KEYS.ROOM_CURRENT_PETS]: {
    stepKey: STEP_KEYS.ROOM_CURRENT_PETS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoomCurrentPets({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (property: Property) => !!property.roomForRent,
  },

  [STEP_KEYS.ROOM_UTILITIES_AMOUNT]: {
    stepKey: STEP_KEYS.ROOM_UTILITIES_AMOUNT,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RoomUtilitiesAmount({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.RENT_DETAILS,
    shouldRender: (property: Property) => !!property.roomForRent,
  },

  // Standard property info steps (also used for room-for-rent)
  [STEP_KEYS.PROPERTY_INFO]: {
    stepKey: STEP_KEYS.PROPERTY_INFO,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PropertyInfo({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: () => true,
  },

  [STEP_KEYS.HIDE_PROPERTY_ADDRESS]: {
    stepKey: STEP_KEYS.HIDE_PROPERTY_ADDRESS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => HidePropertyAddress({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_INFO,
    shouldRender: (_property, flags) => !flags.canAccessFastPost,
  },

  [STEP_KEYS.DESCRIBE_PROPERTY]: {
    stepKey: STEP_KEYS.DESCRIBE_PROPERTY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => DescribeProperty({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (property: Property) => !property.roomForRent,
  },

  // Rent details steps
  [STEP_KEYS.MONTHLY_RENT_AND_SECURITY_DEPOSIT]: {
    stepKey: STEP_KEYS.MONTHLY_RENT_AND_SECURITY_DEPOSIT,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => MonthlyRentAndSecurityDeposit({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.RENT_DETAILS,
    shouldRender: () => true,
  },

  [STEP_KEYS.RENT_DETAILS_AND_COSTS]: {
    stepKey: STEP_KEYS.RENT_DETAILS_AND_COSTS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RentDetailsAndCosts({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.RENT_DETAILS_AND_COSTS,
    shouldRender: (_property, flags) => flags.canAccessAIListingCreation,
  },

  [STEP_KEYS.WHEN_IS_PROPERTY_AVAILABLE]: {
    stepKey: STEP_KEYS.WHEN_IS_PROPERTY_AVAILABLE,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => WhenIsPropertyAvailable({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.RENT_DETAILS,
    shouldRender: (_property, flags) => !flags.canAccessFastPost,
  },

  // Media steps
  [STEP_KEYS.ADD_PHOTOS]: {
    stepKey: STEP_KEYS.ADD_PHOTOS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => AddPhotos({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.MEDIA,
    shouldRender: () => true,
  },

  // Amenities steps
  [STEP_KEYS.AMENITIES_INTERIOR]: {
    stepKey: STEP_KEYS.AMENITIES_INTERIOR,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => AmenitiesInterior({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.AMENITIES,
    shouldRender: () => true,
  },

  [STEP_KEYS.AMENITIES_PROPERTY]: {
    stepKey: STEP_KEYS.AMENITIES_PROPERTY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => AmenitiesProperty({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.AMENITIES,
    shouldRender: () => true,
  },

  [STEP_KEYS.DESCRIPTION_AND_AMENITIES]: {
    stepKey: STEP_KEYS.DESCRIPTION_AND_AMENITIES,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => DescriptionAndAmenities({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.DESCRIPTION_AND_AMENITIES,
    shouldRender: (_property, flags) => flags.canAccessAIListingCreation,
  },

  // Screening criteria steps
  [STEP_KEYS.FINANCIAL_EXPECTATIONS]: {
    stepKey: STEP_KEYS.FINANCIAL_EXPECTATIONS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => FinancialExpectations({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.SCREENING_CRITERIA,
    shouldRender: (_property, flags) => flags.canAccessScreeningCriteria,
  },

  [STEP_KEYS.PET_POLICY_AND_PET_FEE]: {
    stepKey: STEP_KEYS.PET_POLICY_AND_PET_FEE,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PetPolicyAndPetFee({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.SCREENING_CRITERIA,
    shouldRender: (_property, flags) => flags.canAccessScreeningCriteria,
  },

  [STEP_KEYS.SCREENING_CRITERIA_AND_PET_POLICY]: {
    stepKey: STEP_KEYS.SCREENING_CRITERIA_AND_PET_POLICY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => ScreeningCriteriaAndPolicies({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.SCREENING_CRITERIA_AND_PET_POLICY,
    shouldRender: (_property, _flags, flowType) => flowType === FlowType.AI_LISTING_CREATION,
  },

  [STEP_KEYS.PET_POLICY]: {
    stepKey: STEP_KEYS.PET_POLICY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PetPolicy({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.RENT_DETAILS,
    shouldRender: (_property, flags) => !flags.canAccessScreeningCriteria,
  },

  // Costs and fees steps
  [STEP_KEYS.COSTS_AND_FEES]: {
    stepKey: STEP_KEYS.COSTS_AND_FEES,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => CostsAndFees({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.COSTS_AND_FEES,
    shouldRender: (_property, flags) => flags.canAccessSFCostsAndFees,
  },

  // Final details steps
  [STEP_KEYS.LEASE_DURATION]: {
    stepKey: STEP_KEYS.LEASE_DURATION,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => LeaseDuration({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (_property, flags) => !flags.canAccessFastPost,
  },

  [STEP_KEYS.LEASE_DESCRIPTION]: {
    stepKey: STEP_KEYS.LEASE_DESCRIPTION,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => LeaseTerms({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: () => true,
  },

  [STEP_KEYS.WHO_IS_LISTING_PROPERTY]: {
    stepKey: STEP_KEYS.WHO_IS_LISTING_PROPERTY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => WhoIsListingProperty({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: () => true,
  },

  [STEP_KEYS.PHONE_NUMBER]: {
    stepKey: STEP_KEYS.PHONE_NUMBER,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PhoneNumber({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: () => true,
  },

  [STEP_KEYS.ALLOW_CONTACT_BY_PHONE]: {
    stepKey: STEP_KEYS.ALLOW_CONTACT_BY_PHONE,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => AllowContactByPhone({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (property, flags) =>
      !flags.canAccessFastPost && property.forRentBy !== 'agent' && !property.restrictions?.isHidePhoneRestricted,
  },

  // Tour scheduling steps
  [STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingBookingMethod({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (_property, flags) => flags.canAccessPostingPathTourScheduling,
  },

  [STEP_KEYS.TOUR_SCHEDULING_TIMEZONE]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_TIMEZONE,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingTimezone({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (_property, flags, _flowType, userSelections) =>
      flags.canAccessPostingPathTourScheduling && !!userSelections?.tourBookingMethod,
  },

  [STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingAvailability({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (_property, flags, _flowType, userSelections) =>
      flags.canAccessPostingPathTourScheduling && !!userSelections?.tourBookingMethod,
  },

  // Tour scheduling steps for relisting (reuse same components — they handle relisting via canAccessRelistingFlow)
  [STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD_RELIST]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingBookingMethod({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.TOUR_BOOKING,
    shouldRender: (_property, flags) => flags.canAccessPostingPathTourScheduling,
  },

  [STEP_KEYS.TOUR_SCHEDULING_TIMEZONE_RELIST]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_TIMEZONE_RELIST,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingTimezone({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.TOUR_BOOKING,
    shouldRender: (_property, flags, _flowType, userSelections) =>
      flags.canAccessPostingPathTourScheduling && !!userSelections?.tourBookingMethod,
  },

  [STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY_RELIST]: {
    stepKey: STEP_KEYS.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => TourSchedulingAvailability({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.TOUR_BOOKING,
    shouldRender: (_property, flags, _flowType, userSelections) =>
      flags.canAccessPostingPathTourScheduling && !!userSelections?.tourBookingMethod,
  },

  // Fast post and review steps
  [STEP_KEYS.FAST_POST_REVIEW]: {
    stepKey: STEP_KEYS.FAST_POST_REVIEW,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => FastPostReview({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.FINAL_DETAILS,
    shouldRender: (_property, flags) => flags.canAccessFastPost,
  },

  [STEP_KEYS.REVIEW]: {
    stepKey: STEP_KEYS.REVIEW,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => Review({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.REVIEW,
    shouldRender: () => true,
  },

  [STEP_KEYS.PAY_AND_PUBLISH]: {
    stepKey: STEP_KEYS.PAY_AND_PUBLISH,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PayAndPublish({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PAY_AND_PUBLISH,
    shouldRender: () => true,
  },

  // AI flow review & publish steps (reuse the same components — they share one "Review & publish" progress step)
  [STEP_KEYS.REVIEW_AI]: {
    stepKey: STEP_KEYS.REVIEW_AI,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => Review({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.REVIEW_AND_PUBLISH,
    shouldRender: (_property, flags) => flags.canAccessAIListingCreation,
  },

  [STEP_KEYS.PAY_AND_PUBLISH_AI]: {
    stepKey: STEP_KEYS.PAY_AND_PUBLISH_AI,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => PayAndPublish({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.REVIEW_AND_PUBLISH,
    shouldRender: (_property, flags) => flags.canAccessAIListingCreation,
  },

  // Relisting steps
  [STEP_KEYS.UPDATE_RECENTLY_CHANGED]: {
    stepKey: STEP_KEYS.UPDATE_RECENTLY_CHANGED,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RelistConfirmDetails({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.UPDATE,
    shouldRender: (_property, flags) => flags.canAccessRelistingFlow,
  },

  [STEP_KEYS.CONSIDER_ADDITIONAL_DETAILS]: {
    stepKey: STEP_KEYS.CONSIDER_ADDITIONAL_DETAILS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => RelistAdditionalDetails({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.UPDATE,
    shouldRender: (_property, flags) => flags.canAccessRelistingFlow,
  },

  // Multiplex listing steps
  [STEP_KEYS.MULTIPLEX_PROPERTY_DETAILS]: {
    stepKey: STEP_KEYS.MULTIPLEX_PROPERTY_DETAILS,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => MultiplexPropertyDetails({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PROPERTY_DETAILS,
    shouldRender: (_property, flags) => flags.canAccessMultiplexListingFlow,
  },

  [STEP_KEYS.MULTIPLEX_OVERVIEW]: {
    stepKey: STEP_KEYS.MULTIPLEX_OVERVIEW,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => MultiplexOverview({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.MULTIPLEX_OVERVIEW,
    shouldRender: (_property, flags) => flags.canAccessMultiplexListingFlow,
  },

  [STEP_KEYS.MULTIPLEX_LISTING_PUBLISHED]: {
    stepKey: STEP_KEYS.MULTIPLEX_LISTING_PUBLISHED,
    wizardBodyTemplate: (props: WizardBodyTemplateProps) => MultiplexListingPublished({ ...props }),
    progressStepKey: PROGRESS_STEP_KEYS.PUBLISHED,
    shouldRender: (_property, flags) => flags.canAccessMultiplexListingFlow,
  },
};

/**
 * Get active steps for current property and flags
 * Replaces legacy CONFIG manipulation with pure function filtering
 */
export function getActiveSteps(
  property: Property,
  flags: FeatureFlags,
  flowType: FlowType,
  userSelections?: UserSelections,
): Array<WizardStepType> {
  const order = STEP_ORDER[flowType];

  // Filter steps that should render, then restrict to flow-specific order (matches legacy config per flow)
  const visibleSteps = Object.values(ALL_STEPS)
    .filter((step) => step.shouldRender(property, flags, flowType, userSelections))
    .filter((step) => order.includes(step.stepKey));

  visibleSteps.sort((a, b) => order.indexOf(a.stepKey) - order.indexOf(b.stepKey));

  return visibleSteps;
}

/**
 * Filters progress groups to only include those with at least one active step
 * Matches legacy behavior where progress groups are removed when all their steps are hidden
 */
export function getActiveProgressGroups(
  activeSteps: Array<WizardStepType>,
  flowType: FlowType,
): Array<{ key: string; label: string }> {
  const allProgressGroups = PROGRESS_GROUPS[flowType];
  const activeProgressStepKeys = new Set(activeSteps.map((step) => step.progressStepKey));

  return allProgressGroups.filter((group) => activeProgressStepKeys.has(group.key));
}

export const STEP_TO_PROGRESS_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(ALL_STEPS).map(([key, step]) => [key, step.progressStepKey]),
);
